import moment from 'moment-timezone'
import { isEmpty, get } from 'lodash/fp'
import DOMPurify from 'dompurify'

const LOCALE_MAP = {
  THB: 'th-TH',
  CZK: 'cs-CZ',
  ZAR: 'af-ZA',
  SEK: 'en-SE',
}
const CommonFunctions = {
  data: function () {
    return {
      myLoader: 0,
    }
  },
  computed: {
    acceptDeposit() {
      const denyChannels = isEmpty(
        this.reservation.checkin_info?.deposit_deny_channels
      )
        ? ['Airbnb', 'flipkey.com', 'TripAdvisor', 'PlumGuide']
        : this.reservation.checkin_info.deposit_deny_channels
      return (
        this.reservation.checkin_info &&
        this.reservation.checkin_info.accept_deposit_payments &&
        !denyChannels.includes(this.reservation.source)
      )
    },
    shouldPayDeposit() {
      return (
        !this.reservation.pre_check_in_info?.unlock_portal_access &&
        this.acceptDeposit &&
        this.$store.state.tourist.shouldPlaceDeposit
      )
    },
    acceptAccommodation() {
      return (
        this.reservation.checkin_info &&
        ((this.reservation.checkin_info.accept_portal_payments &&
          !this.reservation.checkin_info.accept_portal_payments_deny_channels?.includes(
            this.reservation.source
          )) ||
          this.reservation.checkin_info.allow_manual_card_charge)
      )
    },
    shouldPayAccommodation() {
      const has_balance = this.reservation.balance_due > 0
      const is_local = this.reservation.extra_data?.is_local
      const check_in_payment_threshold =
        this.reservation.checkin_info?.partial_reservation_days_threshold
      if (
        is_local &&
        has_balance &&
        this.acceptAccommodation &&
        !this.reservation.pre_check_in_info?.unlock_portal_access
      ) {
        return true
      }
      const partialPaid = this.reservation.payment_items?.find(
        item => item.status === 'SUCCEEDED'
      )
      const closeCheckIn = moment(this.reservation.check_in).isBefore(
        moment().add(check_in_payment_threshold, 'day')
      )
      return (
        !this.reservation.pre_check_in_info?.unlock_portal_access &&
        this.acceptAccommodation &&
        this.reservation.is_merchant &&
        !this.reservation.use_vcc &&
        this.reservation.balance_due > 0 &&
        this.reservation.payment_intents.find(
          intent =>
            intent.metadata.type === 'accommodation' &&
            (intent.status === 'requires_source' ||
              intent.status === 'requires_payment_method')
        ) &&
        (!partialPaid || (partialPaid && closeCheckIn))
      )
    },
  },
  methods: {
    isGuestIdDocsDeniedForChannel(checkinInfo) {
      const denyChannels = checkinInfo?.guest_id_docs_deny_channels
      const denyList = Array.isArray(denyChannels) ? denyChannels : []
      if (isEmpty(denyList)) return false
      const source = this.$store.getters.currentReservation?.source
      if (!source) return false
      return denyList.includes(source)
    },
    formatDate(date, format, timezone) {
      if (timezone) {
        return moment(date).tz(timezone).format(format)
      }
      return moment(date).format(format)
    },
    formatReservationPeriod(reservation, format) {
      return `${this.formatDate(
        reservation.check_in,
        format
      )} - ${this.formatDate(reservation.check_out, format)} `
    },
    toCurrency(amount, config = { currency: null, maxDigits: 2 }) {
      if (!amount && amount !== 0) return null
      const currency =
        config.currency || get('config.currency', window) || 'USD'
      const locale = LOCALE_MAP[currency.toUpperCase()] || 'en'
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: config.maxDigits || 2,
      }).format(amount)
    },
    toDollar(amount) {
      return this.toCurrency(amount, 2, 'USD')
    },
    formatImageUrl(url, width) {
      if (!url) return url
      const secure = url.replace(
        /^http:\/\/(res\.cloudinary\.com)/,
        'https://$1'
      )
      if (
        !secure.includes('res.cloudinary.com') ||
        !secure.includes('/upload/')
      )
        return secure
      if (/\/upload\/[^/]*f_auto/.test(secure)) return secure
      const parts = ['f_auto', 'q_auto']
      if (width) parts.push(`c_limit,w_${width}`)
      return secure.replace('/upload/', `/upload/${parts.join(',')}/`)
    },
    isPdf(src) {
      if (src && src.indexOf('.pdf') != -1) {
        return true
      } else {
        return false
      }
    },
    isVideo(src) {
      return (
        src &&
        (src.indexOf('.m4v') !== -1 ||
          src.indexOf('.avi') !== -1 ||
          src.indexOf('.mpg') !== -1 ||
          src.indexOf('.mp4') !== -1 ||
          src.indexOf('.mov') !== -1)
      )
    },
    isYouTubeEmbed(src) {
      return src && src.includes('youtube.com/embed/')
    },
    hasAbility(ability) {
      const { user } = this.$store.state.user
      if (isEmpty(ability)) {
        return true
      }
      if (user && user.abilities) {
        if (user.abilities.includes('super-admin')) {
          return true
        }
        if (Array.isArray(ability)) {
          return ability.some(ab => user.abilities.includes(ab))
        }
        return user.abilities.includes(ability)
      } else {
        return false
      }
    },
    safeHtml(html) {
      if (!html) return ''
      return DOMPurify.sanitize(html)
    },
    listingTitle(reservation, dateDependent = false) {
      if (!reservation) {
        return ''
      }

      const { checkin_info } = reservation
      const useTitle = checkin_info?.use_listing_title

      if (!dateDependent) {
        return useTitle
          ? reservation.listing.title
          : reservation.listing.nickname
      }

      const { title, nickname, hotel_title, hotel_name } = reservation.listing
      const hide_hotel_units = checkin_info && checkin_info.hide_hotel_units
      const check_in = moment(reservation.check_in)
      const before_checkin_day = moment().isBefore(check_in.startOf('day'))
      const hotelTitle = useTitle ? hotel_title : hotel_name
      const listingTitle = useTitle ? title : nickname
      return (
        (hide_hotel_units && before_checkin_day && hotelTitle) || listingTitle
      )
    },
  },
}

export default CommonFunctions
